import { ActionIcon, Box, Drawer, Group, TextInput } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import Invoice from 'app/Private/Invoices/Invoice';
import type { CollectionStepIndex } from 'components/CollectionStep';
import { FilterButton } from 'components/FilterButton';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { isDefined } from 'remeda';
import { STEP_TO_STEP_THEME } from 'shared';
import type { LightDebtor } from 'shared/hooks/use-load-light-debtors';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import { useLoadKanbanInvoices } from '../api';
import { InvoiceKanbanCard, InvoiceKanbanFilters, Kanban, KanbanLoadingState } from '../components';
import { countActiveFilters } from '../utils/filters';

type InvoicesKanbanProps = {
  // When provided, the kanban is rendered in "embedded mode"
  lockedDebtor?: LightDebtor;
};

export const InvoicesKanban = ({ lockedDebtor }: InvoicesKanbanProps = {}) => {
  const { t } = useTranslation();
  const isDebtorLocked = isDefined(lockedDebtor);

  const { columns, filtersForm } = useLoadKanbanInvoices({
    initialFilters: isDebtorLocked ? { debtorId: lockedDebtor.id } : undefined,
  });
  const [isFiltersDrawerOpen, filtersDrawerHandlers] = useDisclosure(false);

  // Skip the global 'f' hotkey when embedded — avoids stealing it from sibling client tabs
  useHotkeys(isDebtorLocked ? [] : [['f', filtersDrawerHandlers.toggle]]);

  const isFetching = Object.values(columns).some(column => column.isInvoiceColumnFetching);
  const areAllColumnsPopulated = Object.values(columns).every(column =>
    isDefined(column.invoiceColumn),
  );
  if (!areAllColumnsPopulated)
    return <KanbanLoadingState title={t(i18nKeys.KANBAN.INVOICE.TITLE)} columnCount={6} />;

  const activeFiltersCount = countActiveFilters(filtersForm.values, [
    'column',
    'remainingBalance.operator',
    ...(isDebtorLocked ? (['debtorId'] as const) : []),
  ]);

  const openInvoiceModal = (invoiceId: number) => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={invoiceId} modal />,
    });
  };

  const cols = Object.values(columns).map((column, colIndex) => {
    const cards = column.invoiceColumn?.invoices.map(invoice => (
      <Kanban.Card key={invoice.id} onClick={() => openInvoiceModal(invoice.id)}>
        <InvoiceKanbanCard invoice={invoice} />
      </Kanban.Card>
    ));

    return (
      <Kanban.Column
        isLoading={isFetching}
        key={colIndex}
        step={colIndex as CollectionStepIndex}
        total={column.invoiceColumn!.total.amount ?? 0}
        currency={column.invoiceColumn!.total.currency ?? 'EUR'}
        color={STEP_TO_STEP_THEME[colIndex as CollectionStepIndex].color}
      >
        {cards}
      </Kanban.Column>
    );
  });

  const actions = (
    <>
      <TextInput
        w={250}
        placeholder={t(i18nKeys.KANBAN.FILTERS.INVOICE_NAME_PH)}
        rightSection={
          filtersForm.values.reference ? (
            <ActionIcon
              variant="light"
              c="gray"
              onClick={() => filtersForm.setFieldValue('reference', '')}
            >
              <IconX stroke={1.5} size={16} />
            </ActionIcon>
          ) : null
        }
        {...filtersForm.getInputProps('reference')}
      />
      {!isDebtorLocked && (
        <FilterButton
          activeFiltersCount={activeFiltersCount}
          openFilters={filtersDrawerHandlers.open}
          resetFilters={() => filtersForm.reset()}
        />
      )}
    </>
  );

  return (
    <>
      <Box h="60px">
        {isDebtorLocked ? (
          <Group justify="flex-start" align="center" h="100%" gap="sm">
            {actions}
          </Group>
        ) : (
          <PageTitle>
            {t(i18nKeys.KANBAN.INVOICE.TITLE)}
            <PageTitle.Actions>{actions}</PageTitle.Actions>
          </PageTitle>
        )}
      </Box>
      <Box h="calc(100% - 60px)">
        <Kanban>{cols}</Kanban>
      </Box>
      {!isDebtorLocked && (
        <Drawer
          position="right"
          opened={isFiltersDrawerOpen}
          onClose={filtersDrawerHandlers.close}
          title={t(i18nKeys.FILTERS)}
          keepMounted
          padding="lg"
          styles={{
            title: {
              fontSize: '1.5rem',
            },
          }}
        >
          <InvoiceKanbanFilters form={filtersForm} />
        </Drawer>
      )}
    </>
  );
};
